import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Mail } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { WHATSAPP_URL, CONTACT_EMAIL } from "@/data/translations";
import { trackCtaClick, getEligibilityUrl } from "@/lib/analytics";

/**
 * CTA FINALE — "Votre avenir commence ici"
 * Full-viewport dark section. Enormous Canela headline at 9vw, partially cropped.
 * Saffron underline that animates in. Single CTA that redirects to external
 * assessment app. Contact links (WhatsApp / email) live in the sidebar.
 */
const CTABanner = () => {
  const { t, lang } = useLang();

  return (
    <section
      id="contact"
      data-testid="cta-banner"
      className="relative bg-[#0D1117] py-32 sm:py-44 overflow-hidden"
    >
      <div className="noise-soft" />
      {/* faint saffron glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 50% 60% at 30% 40%, rgba(232,160,69,0.08) 0%, transparent 70%)",
        }}
      />

      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="font-mono-accent text-[10px] text-[#E8A045] mb-5">
          {t.cta.eyebrow}
        </div>

        {/* Headline — proportionate to the rest of the page */}
        <h2
          data-testid="cta-headline"
          className="font-display text-[8vw] sm:text-[6.5vw] lg:text-[5vw] leading-[0.95] tracking-[-0.03em] text-[#F5F2EE] mb-2 max-w-[95%]"
          style={{ letterSpacing: "-0.03em" }}
        >
          {(() => {
            const words = t.cta.headline.split(" ");
            const first = words.slice(0, 3).join(" ");
            const rest = words.slice(3).join(" ");
            return (
              <>
                <span className="block">{first}</span>
                {rest && (
                  <span className="block italic text-[#9B7548]">{rest}</span>
                )}
              </>
            );
          })()}
        </h2>

        {/* Animated saffron underline — scaled to the smaller headline */}
        <motion.span
          initial={{ width: 0 }}
          whileInView={{ width: 110 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="block h-[2px] bg-[#E8A045] mb-10 mt-3"
        />

        {/* CTA + offer card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start mt-6">
          <div className="lg:col-span-7">
            <div
              data-testid="consultation-offer-card"
              className="border border-[#F5F2EE]/10 bg-[#F5F2EE]/[0.03] rounded-2xl p-6 sm:p-8 mb-8 max-w-xl"
            >
              <div className="mb-6">
                <div className="font-mono-accent text-[10px] text-[#F5F2EE]/55 mb-1">
                  {t.cta.durationLabel}
                </div>
                <div
                  data-testid="consultation-duration"
                  className="font-display text-2xl sm:text-3xl text-[#F5F2EE] leading-none"
                >
                  {t.cta.durationValue}
                </div>
              </div>

              <div className="font-mono-accent text-[10px] text-[#E8A045] mb-3">
                {t.cta.includesLabel}
              </div>
              <ul className="space-y-2 mb-6">
                {t.cta.includes.map((item, i) => (
                  <li
                    key={i}
                    data-testid={`consultation-includes-${i}`}
                    className="flex items-start gap-3 font-body text-sm text-[#F5F2EE]/80 leading-relaxed"
                  >
                    <span className="mt-[7px] h-[5px] w-[5px] rounded-full bg-[#E8A045] shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <a
                href={getEligibilityUrl(lang)}
                data-testid="cta-submit"
                onClick={() => trackCtaClick("cta-banner")}
                className="inner-border-btn group inline-flex items-center justify-center gap-3 bg-[#2FBFAD] hover:bg-[#239B8C] text-[#0D1117] px-9 py-4 rounded-full font-mono-accent text-[11px] font-medium tracking-wider transition-colors whitespace-nowrap"
              >
                <span>{t.cta.buttonLabel}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
              </a>
            </div>

            <p className="font-body text-sm text-[#F5F2EE]/55 max-w-md leading-relaxed">
              {t.cta.sub}
            </p>
          </div>

          {/* Contact links */}
          <div className="lg:col-span-5 lg:border-l lg:border-[#F5F2EE]/10 lg:pl-10">
            <div className="font-mono-accent text-[10px] text-[#E8A045] mb-5">
              {t.footer.contact}
            </div>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp"
              className="group flex items-center gap-3 text-[#F5F2EE]/85 hover:text-[#E8A045] transition-colors mb-3"
            >
              <MessageCircle className="w-4 h-4 text-[#E8A045]" strokeWidth={1.5} />
              <span className="font-body text-base">{t.cta.whatsapp}</span>
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="cta-email-link"
              className="group flex items-center gap-3 text-[#F5F2EE]/85 hover:text-[#E8A045] transition-colors"
            >
              <Mail className="w-4 h-4 text-[#E8A045]" strokeWidth={1.5} />
              <span className="font-body text-base">{CONTACT_EMAIL}</span>
            </a>
            <div className="mt-8 font-mono-accent text-[10px] text-[#F5F2EE]/45">
              {t.cta.response}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTABanner;
